import { Dispatch, SetStateAction } from 'react';
import {
	Modal,
	TextField,
	Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAddTemplateMutation, useDeleteTemplateMutation } from '../services/settings';
import { showError, showToast } from '../constants/toasts';

interface Props {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>
    onSuccess?: () => void;
    templateId?: number; // if provided, enable Delete action

}

export default function AddTemplate({ open, onClose, setOpen, onSuccess, templateId }: Props) {

    const [addTemplate, { isLoading } ] = useAddTemplateMutation();
    const [deleteTemplate, { isLoading: isDeleting } ] = useDeleteTemplateMutation();

    const formik = useFormik({
        initialValues: {
            title: '',
            message: '',
        },
        validationSchema: Yup.object({
            title: Yup.string().required('Title is required'),
            message: Yup.string().required('Message is required'),
        }),
        onSubmit: async (values, { resetForm }) => {
            try {
                const res: any = await addTemplate({ body: values }).unwrap();
                if (res?.status === 201) {
                    showToast(res?.message || 'Template added');
                    resetForm();
                    setOpen(false);
                    onSuccess && onSuccess();
                } else {
                    showError(res?.message || 'Failed to add template');
                }
            } catch (e: any) {
                showError(e?.data?.message || 'Failed to add template');
            }
        },
    });

    const handleDelete = async () => {
        if (!templateId) return;
        try {
            const res: any = await deleteTemplate({ id: templateId }).unwrap();
            if (res?.status === 200 || res?.status === 204) {
                showToast(res?.message || 'Template deleted');
                setOpen(false);
                onSuccess && onSuccess();
            } else {
                showError(res?.message || 'Failed to delete template');
            }
        } catch (e: any) {
            showError(e?.data?.message || 'Failed to delete template');
        }
    };

    return (
        <Modal
            className="modal alertConfig_modal"
            open={open}
            onClose={onClose}
        >
            <div className="modal-dialog">
                <div className="modal-body">
                    <div className="modal_title">
                        <h2>Add Template</h2>
                        <div className="btn-close" onClick={() => setOpen(false)}>
                            <CloseIcon />
                        </div>
                    </div>

                    <form className="form" onSubmit={formik.handleSubmit}>
                        <div className="control_group">
                            <label>Title</label>
                            <TextField
                                fullWidth
                                hiddenLabel
                                placeholder="Enter Title"
                                name="title"
                                value={formik.values.title}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.title && Boolean(formik.errors.title)}
                                helperText={formik.touched.title && formik.errors.title}
                            />
                        </div>
                        <div className="control_group">
                            <label>Message</label>
                            <TextField
                                fullWidth
                                hiddenLabel
                                placeholder="Enter Message"
                                multiline
                                rows={2}
                                name="message"
                                value={formik.values.message}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.message && Boolean(formik.errors.message)}
                                helperText={formik.touched.message && formik.errors.message}
                            />
                        </div>
                        <div className="footer_btn_flex">
                            <Button className="btn btn_grey" onClick={() => setOpen(false)} disabled={isLoading || isDeleting}>Cancel</Button>
                            {templateId && (
                                <Button className="btn btn_grey" onClick={handleDelete} disabled={isDeleting}>
                                    Delete
                                </Button>
                            )}
                            <Button className="btn btn_primary" type="submit" disabled={isLoading}>Submit</Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    );
}
